/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useReducer, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    CheckCircle, User, Settings, BookOpen, MessageSquare, ShieldAlert, Database, ChevronsRight, Cog, Eye
} from 'lucide-react';

import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Button
} from 'shared/components/ui/button';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';

import {
    Context
} from 'shared/context/GlobalState';
import {
    assistantInstanceService, opcodeAssistantService, channelService, redisCollectionService, apiManagerService,
} from 'shared/api';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    AssistantStatus
} from 'shared/api/services/bexService.types';

import {
    Assistant, Opcode, OpcodeEntry, makeEmptyAssistant, INPUT_MAPPING_PATTERN
} from '../types/builderAssistant.types';
import OpCodeSelectionModal from './OpCodeSelectionModal';
import InputMappingEditor from './InputMappingEditor';
import EntitySchemaBuilder from './EntitySchemaBuilder';
import MemoryConfigSection from './MemoryConfigSection';
import GovernanceSection from './GovernanceSection';
import ChipInput from './ChipInput';

type Props = {
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    setAssistantInstances: React.Dispatch<React.SetStateAction<Assistant[]>>;
};

// Reducer only holds the form data; UI state (step, loading, opcodes list)
// stays local to keep reducer narrow.
type Action =
    | { type: 'SET_FIELD'; path: (string | number)[]; value: unknown }
    | { type: 'SET_FORM'; form: Assistant }
    | { type: 'ADD_OPCODE'; entry: OpcodeEntry }
    | { type: 'UPDATE_OPCODE'; opcode_id: string; patch: Partial<OpcodeEntry> }
    | { type: 'REMOVE_OPCODE'; opcode_id: string };

function setDeep(obj: any, path: (string | number)[], value: unknown): any {
    if (path.length === 0) return value;
    const [head, ...rest] = path;
    const next = Array.isArray(obj) ? [...obj] : {
 ...obj
};
    next[head] = setDeep(obj?.[head] ?? (typeof rest[0] === 'number' ? [] : {}), rest, value);
    return next;
}

function reducer(state: Assistant, action: Action): Assistant {
    switch (action.type) {
        case 'SET_FIELD':
            return setDeep(state, action.path, action.value);
        case 'SET_FORM':
            return action.form;
        case 'ADD_OPCODE':
            if (state.opcodes.some(e => e.opcode_id === action.entry.opcode_id)) return state;
            return {
 ...state,
opcodes: [...state.opcodes, action.entry]
};
        case 'UPDATE_OPCODE':
            return {
                ...state,
                opcodes: state.opcodes.map(e =>
                    e.opcode_id === action.opcode_id ? {
 ...e,
...action.patch
} : e
                ),
            };
        case 'REMOVE_OPCODE':
            return {
                ...state,
                opcodes: state.opcodes.filter(e => e.opcode_id !== action.opcode_id),
                default_opcode: state.default_opcode === action.opcode_id ? null : state.default_opcode,
            };
        default:
            return state;
    }
}

// Typed Input is Step 2 (before OpCodes) so the Input Mapping editor on the
// OpCodes step can pick from user-declared context/propensity/entity fields.
const STEP_TITLES = [
    'Identity',
    'Typed Input',
    'OpCodes',
    'Knowledge & Data',
    'Instructions',
    'Channels',
    'Memory',
    'Governance',
    'Review & Create',
];

const STEP_ICONS = [
    <User key="i" className="w-4 h-4" />,
    <BookOpen key="t" className="w-4 h-4" />,
    <Settings key="o" className="w-4 h-4" />,
    <Database key="k" className="w-4 h-4" />,
    <MessageSquare key="n" className="w-4 h-4" />,
    <ChevronsRight key="c" className="w-4 h-4" />,
    <Cog key="m" className="w-4 h-4" />,
    <ShieldAlert key="g" className="w-4 h-4" />,
    <Eye key="r" className="w-4 h-4" />,
];

export default function CreateAssistantWizard({
 setShow, setAssistantInstances
}: Props) {
    const {
 oragID, subOragID
} = useParams<{ oragID: string; subOragID: string }>();
    const {
 addNewNotifcation
} = useContext(Context);

    const [state, dispatch] = useReducer(
        reducer,
        makeEmptyAssistant(oragID || '', subOragID || '')
    );

    const [step, setStep] = useState(1);
    const [creating, setCreating] = useState(false);

    // Lazy-loaded reference data. Opcodes come from two sources (classic / json
    // builders) but they're combined into one flat list for selection — the
    // assistant doc doesn't care which builder authored each opcode.
    const [availableOpcodes, setAvailableOpcodes] = useState<Opcode[]>([]);
    const [loadingOpcodes, setLoadingOpcodes] = useState(true);
    const [channels, setChannels] = useState<Array<{ _id: string; topic?: string; address?: string }>>([]);
    const [collections, setCollections] = useState<Array<{ _id: string; name: string; description?: string }>>([]);
    const [apis, setApis] = useState<Array<{ _id: string; name: string; description?: string }>>([]);

    useEffect(() => {
        loadOpcodes();
        loadChannels();
        loadCollections();
        loadApis();
    }, []);

    const loadOpcodes = async () => {
        try {
            setLoadingOpcodes(true);
            // A-0008.03: V1 ("Classic") removed; single source of truth.
            const resJson = await jsonOpcodeService.listJSONOpcodes({
                organizationId: oragID!,
                subOrganizationId: subOragID!,
            });
            const rawJson: any[] = Array.isArray(resJson?.data) ? resJson.data : [];
            const uJson = Array.from(new Map(rawJson.filter((o: any) => o._id || o.opcode_id || o.id).map((o: any) => [(o._id || o.opcode_id || o.id), o])).values());
            const jsonOps: Opcode[] = uJson.map((op: any) => {
                // JSON opcode docs carry `inputs` as either:
                //   (a) array of strings — the input names      (older shape)
                //   (b) array of { name, type, description, ... } objects  (newer)
                // Normalize to a plain string[] of names so the mapping editor
                // always gets a consistent list.
                const rawInputs = op.inputs || op.definitions?.inputs || [];
                const inputs = (rawInputs as any[])
                    .map((i: any) => (typeof i === 'string' ? {
 name: i
} : i))
                    .filter((i: any) => i?.name);
                return {
                    ...op,
                    name: op.title || op.name,
                    opcode_id: op._id || op.opcode_id || op.id,
                    inputs,
                };
            });
            setAvailableOpcodes(jsonOps);
        } catch (err) {
            console.error('Error loading opcodes', err);
            addNewNotifcation('Failed to load opcodes. Please try again.', 'danger');
        } finally {
            setLoadingOpcodes(false);
        }
    };

    const loadChannels = async () => {
        try {
            const res = await channelService.getChannels(oragID || '', subOragID || '');
            const data = (res as any)?.data || [];
            setChannels(Array.isArray(data) ? data : []);
        } catch (err) {
            // Non-fatal: channels are optional; user can skip
            console.warn('Failed to load channels', err);
        }
    };

    const loadCollections = async () => {
        try {
            const res = await redisCollectionService.listCollections(oragID || '', subOragID || '');
            const data: any[] = Array.isArray((res as any)?.data) ? (res as any).data : [];
            setCollections(data.map((c: any) => ({
                _id: c._id,
name: c.name || c._id,
description: c.description || '',
            })));
        } catch (err) {
            console.warn('Failed to load collections', err);
        }
    };

    const loadApis = async () => {
        try {
            const res = await apiManagerService.listApis(oragID || '', subOragID || '');
            const data: any[] = Array.isArray((res as any)?.data) ? (res as any).data : [];
            setApis(data.map((a: any) => ({
                _id: a._id,
name: a.name || a._id,
                description: `${a.method || 'GET'} ${a.url || ''}`,
            })));
        } catch (err) {
            console.warn('Failed to load APIs', err);
        }
    };

    const setField = (path: (string | number)[], value: unknown) =>
        dispatch({
 type: 'SET_FIELD',
path,
value
});

    // ──────────── Step guards ────────────
    const canAdvance = (): boolean => {
        switch (step) {
            case 1:
                // `title` is the only required identity field. Server
                // auto-generates `assistant_id` (slug) from it on create.
                return !!state.title?.trim();
            case 2:
                // Typed Input — all fields are optional; user can leave empty.
                return true;
            case 3: {
                // OpCodes — must pick at least one opcode + a default.
                if (state.opcodes.length === 0 || !state.default_opcode) return false;
                for (const entry of state.opcodes) {
                    for (const [, mapping] of Object.entries(entry.input_mapping || {})) {
                        if (mapping && !INPUT_MAPPING_PATTERN.test(mapping)) return false;
                    }
                }
                return true;
            }
            case 6: {
                // Restricted + empty list is allowed (but warns). Open is always OK.
                return true;
            }
            default:
                return true;
        }
    };

    const handleCreate = async () => {
        if (!canAdvance()) {
            addNewNotifcation('Please complete required fields before creating', 'warning');
            return;
        }
        setCreating(true);
        try {
            // Create Mongo-side assistant instance record first (channels bind
            // against it). We no longer pass its id into the rich doc — Server
            // generates the slug itself.
            const inst = await assistantInstanceService.addAssistantInstance({
                title: state.title,
                description: state.description,
                status: state.status as AssistantStatus,
                organizationId: oragID,
                subOrganizationId: subOragID,
            });

            // Build rich payload. MUST NOT include `assistant_id` — Server
            // generates the slug from `title` and echoes it back on the
            // response. Also strip server-managed audit fields.
            const {
                assistant_id: _drop1,
                created: _drop2,
                updated: _drop3,
                updatedBy: _drop4,
                ...rest
            } = state;
            void _drop1; void _drop2; void _drop3; void _drop4;
            const payload = {
                ...rest,
                createdBy: inst.data.createdBy || 'builder',
            };

            const res = await opcodeAssistantService.createOpcodeAssistant(payload as any);
            const created = ((res as any)?.data || {}) as Partial<Assistant>;

            // Server returns the auto-generated `assistant_id` on the create
            // response. Fall back to payload state if the response is missing
            // it (should not happen with the new contract).
            const finalAssistant: Assistant = {
                ...state,
                ...(created as Assistant),
                assistant_id: created.assistant_id || '',
            };

            setAssistantInstances(prev => [finalAssistant, ...prev]);
            addNewNotifcation('Assistant created successfully', 'success');
            setShow(false);
        } catch (err: any) {
            console.error('creation error', err);
            const msg = err?.response?.data || err?.message || 'Something went wrong while creating the assistant';
            addNewNotifcation(typeof msg === 'string' ? msg : 'Something went wrong', 'danger');
        } finally {
            setCreating(false);
        }
    };

    const selectedOpcodeIds = state.opcodes.map(e => e.opcode_id);

    const handleOpcodesSelected = (ids: string[]) => {
        // Merge: keep metadata for already-selected entries, seed fresh ones.
        const byId = new Map(state.opcodes.map(e => [e.opcode_id, e] as const));
        const next: OpcodeEntry[] = ids.map(id => byId.get(id) || ({
            opcode_id: id,
            purpose: '',
            description: '',
            input_mapping: {},
        }));
        setField(['opcodes'], next);
    };

    // ──────────── Rendering ────────────

    return (
        <div className="w-full">
            {/* Progress */}
            <div className="mb-4">
                <div className="flex items-center justify-between overflow-x-auto pb-2">
                    {STEP_TITLES.map((label, idx) => {
                        const n = idx + 1;
                        const active = step === n;
                        const done = step > n;
                        return (
                            <React.Fragment key={n}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        // Allow jumping backwards freely; forward only if guards pass
                                        if (n < step) setStep(n);
                                        else if (n === step) return;
                                        else if (canAdvance()) setStep(n);
                                    }}
                                    className="flex flex-col items-center shrink-0 px-1"
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${done ? 'bg-red-700 text-white' : active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                                        {done ? <CheckCircle className="w-4 h-4" /> : STEP_ICONS[idx]}
                                    </div>
                                    <span className={`text-[10px] mt-1 whitespace-nowrap ${active ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                                        {label}
                                    </span>
                                </button>
                                {n < STEP_TITLES.length && <div className={`flex-1 h-0.5 mx-1 ${step > n ? 'bg-red-700' : 'bg-muted'}`} />}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            <ScrollArea className="h-[420px] px-1">
                {/* Step 1: Identity */}
                {step === 1 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Identity</h3>
                        <div>
                            <Label htmlFor="w-title">Assistant Name <small className="text-red-500">*</small></Label>
                            <Input id="w-title" value={state.title} onChange={(e) => setField(['title'], e.target.value)} placeholder="Aria — Customer Support" />
                            <p className="text-xs text-muted-foreground mt-1">
                                A unique internal id is auto-generated from this name on save.
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="w-desc">Description</Label>
                            <Textarea id="w-desc" value={state.description} onChange={(e) => setField(['description'], e.target.value)} rows={2} />
                        </div>
                        <div>
                            <Label htmlFor="w-persona">Persona</Label>
                            <Textarea id="w-persona" value={state.persona} onChange={(e) => setField(['persona'], e.target.value)} placeholder="Friendly, concise, uses the customer's first name." rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label>Version</Label>
                                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                        informational today
                                    </span>
                                </div>
                                <Input value={state.version} onChange={(e) => setField(['version'], e.target.value)} placeholder="1.0.0" />
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={state.status} onValueChange={(v) => setField(['status'], v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">active</SelectItem>
                                        <SelectItem value="inactive">inactive</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Typed Input — defined before OpCodes so the input-mapping
                    editor can offer the user-declared fields as targets. */}
                {step === 2 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Typed Input</h3>
                        <p className="text-xs text-muted-foreground -mt-1">
                            Declare what your assistant accepts in each category. Whatever you put here
                            becomes the set of selectable targets when wiring opcode inputs on the next step.
                        </p>
                        <div>
                            <Label>Declared context keys (soft)</Label>
                            <ChipInput
                                values={state.typed_input.context_keys}
                                onChange={(v) => setField(['typed_input', 'context_keys'], v)}
                                placeholder="language, customer_tier, ..."
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Unknown keys at runtime produce a warning, not a rejection.
                            </p>
                        </div>
                        <div>
                            <Label>Declared propensity keys (soft)</Label>
                            <ChipInput
                                values={state.typed_input.propensity_keys}
                                onChange={(v) => setField(['typed_input', 'propensity_keys'], v)}
                                placeholder="urgency, verbosity, ..."
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label>Intent format</Label>
                                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                        informational today
                                    </span>
                                </div>
                                <Select
                                    value={state.typed_input.intent.format}
                                    onValueChange={(v) => setField(['typed_input', 'intent', 'format'], v)}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="text">text</SelectItem>
                                        <SelectItem value="structured">structured</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Not enforced at runtime — Mind accepts whatever shape you send under <code>typed_input.intent</code>.
                                </p>
                            </div>
                            <div>
                                <Label>Routing mode</Label>
                                <Select
                                    value={state.typed_input.intent.routing_mode}
                                    onValueChange={(v) => setField(['typed_input', 'intent', 'routing_mode'], v)}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="default_only">default_only</SelectItem>
                                        <SelectItem value="intent_match">intent_match</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="pt-2 border-t">
                            <EntitySchemaBuilder
                                value={state.typed_input.entity_schema}
                                onChange={(v) => setField(['typed_input', 'entity_schema'], v)}
                            />
                        </div>
                    </div>
                )}

                {/* Step 3: OpCodes — after Typed Input so InputMappingEditor
                    can target the user-declared context / propensity / entity
                    fields via a dropdown. */}
                {step === 3 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">OpCodes</h3>
                        <div>
                            <Label>Selected opcodes <small className="text-red-500">*</small></Label>
                            <OpCodeSelectionModal
                                disabled={loadingOpcodes}
                                opcodes={availableOpcodes}
                                defaultSelectedOpcodes={selectedOpcodeIds}
                                multiSelect
                                onOpcodeSelect={(res: any) => handleOpcodesSelected(res as string[])}
                            />
                            <div className="flex flex-wrap gap-1 mt-2">
                                {selectedOpcodeIds.length === 0 && <span className="text-xs text-muted-foreground">No opcodes selected</span>}
                                {selectedOpcodeIds.map(id => {
                                    const found = availableOpcodes.find(o => o.opcode_id === id);
                                    return <Badge key={id} variant="secondary" className="text-xs">{found?.name || id}</Badge>;
                                })}
                            </div>
                        </div>

                        <div>
                            <Label>Default opcode <small className="text-red-500">*</small></Label>
                            <Select
                                value={state.default_opcode || ''}
                                onValueChange={(v) => setField(['default_opcode'], v)}
                            >
                                <SelectTrigger><SelectValue placeholder="Pick from selected" /></SelectTrigger>
                                <SelectContent>
                                    {selectedOpcodeIds.map(id => {
                                        const found = availableOpcodes.find(o => o.opcode_id === id);
                                        return <SelectItem key={id} value={id}>{found?.name || id}</SelectItem>;
                                    })}
                                </SelectContent>
                            </Select>
                        </div>

                        {state.opcodes.length > 0 && (
                            <div className="space-y-3 pt-3 border-t mt-3">
                                <div className="flex items-baseline justify-between">
                                    <h4 className="text-sm font-semibold">
                                        Configure opcode(s) ({state.opcodes.length})
                                    </h4>
                                    <span className="text-xs text-muted-foreground">
                                        purpose · description · input mapping
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Purpose is used by intent routing. Input mapping wires each
                                    opcode input to a field you declared in Typed Input
                                    (e.g. <code className="text-[11px]">user_query → context.user_input</code>).
                                </p>
                                {state.opcodes.map(entry => {
                                    const found = availableOpcodes.find(o => o.opcode_id === entry.opcode_id);
                                    const inputs = (found?.inputs || []).map((i: any) => typeof i === 'string' ? i : i?.name).filter(Boolean);
                                    return (
                                        <div key={entry.opcode_id} className="border-2 border-primary/20 rounded-md p-3 space-y-2 bg-primary/[0.02]">
                                            <div className="font-medium text-sm">{found?.name || entry.opcode_id}</div>
                                            <div>
                                                <Label className="text-xs">Purpose <small className="text-muted-foreground">(routing hint)</small></Label>
                                                <Input
                                                    value={entry.purpose}
                                                    onChange={(e) => dispatch({
                                                        type: 'UPDATE_OPCODE',
opcode_id: entry.opcode_id,
                                                        patch: {
 purpose: e.target.value
},
                                                    })}
                                                    placeholder="e.g. look up order status"
                                                    className="text-xs"
                                                />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <Label className="text-xs">Description</Label>
                                                    <span className="text-[9px] uppercase tracking-wider px-1 py-0 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                                        display only
                                                    </span>
                                                </div>
                                                <Textarea
                                                    value={entry.description}
                                                    onChange={(e) => dispatch({
                                                        type: 'UPDATE_OPCODE',
opcode_id: entry.opcode_id,
                                                        patch: {
 description: e.target.value
},
                                                    })}
                                                    rows={2}
                                                    className="text-xs"
                                                />
                                            </div>
                                            <InputMappingEditor
                                                opcodeInputs={inputs as string[]}
                                                typedInput={state.typed_input}
                                                value={entry.input_mapping}
                                                onChange={(next) => dispatch({
                                                    type: 'UPDATE_OPCODE',
opcode_id: entry.opcode_id,
                                                    patch: {
 input_mapping: next
},
                                                })}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Step 4: Knowledge & Data — pickers sourced from
                    redisCollectionService + apiManagerService for this sub-org. */}
                {step === 4 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Knowledge & Data Connections</h3>
                        <div>
                            <Label>Knowledge collections</Label>
                            <KnowledgeEditor
                                value={state.knowledge}
                                onChange={(v) => setField(['knowledge'], v)}
                                options={collections}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Runtime enforces: opcodes that use a RAG step must reference a declared collection here.
                            </p>
                        </div>
                        <div>
                            <Label>Data connections (APIs)</Label>
                            <DataConnectionsEditor
                                value={state.data_connections}
                                onChange={(v) => setField(['data_connections'], v)}
                                options={apis}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Runtime enforces: opcodes that use an API step must reference a declared connection here.
                            </p>
                        </div>
                    </div>
                )}

                {/* Step 5: Instructions */}
                {step === 5 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Instructions (Identity Context)</h3>
                        <p className="text-xs text-muted-foreground">
                            This employee&apos;s identity (system prompt, persona, guidelines, content policy) is added to the
                            start of every instruction prompt automatically when it runs &mdash; no token in the prompt is needed.
                        </p>
                        <div>
                            <Label>System prompt</Label>
                            <Textarea
                                value={state.instructions.system_prompt || ''}
                                onChange={(e) => setField(['instructions', 'system_prompt'], e.target.value || null)}
                                rows={4}
                                placeholder="You are Aria, a customer support assistant for ACME..."
                            />
                        </div>
                        <div>
                            <Label>Guidelines</Label>
                            <Textarea
                                value={state.instructions.guidelines || ''}
                                onChange={(e) => setField(['instructions', 'guidelines'], e.target.value || null)}
                                rows={3}
                            />
                        </div>
                        <div>
                            <Label>Content policy</Label>
                            <Textarea
                                value={state.instructions.content_policy || ''}
                                onChange={(e) => setField(['instructions', 'content_policy'], e.target.value || null)}
                                rows={3}
                            />
                        </div>
                    </div>
                )}

                {/* Step 6: Channels */}
                {step === 6 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Channels</h3>
                        <div className="space-y-2">
                            <Label>Mode</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setField(['channels', 'mode'], 'restricted')}
                                    className={`p-3 rounded-md border text-left ${state.channels.mode === 'restricted' ? 'border-primary bg-primary/5' : 'border-border'}`}
                                >
                                    <div className="font-medium text-sm">Restricted</div>
                                    <div className="text-xs text-muted-foreground">Only the channels below may invoke this assistant.</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setField(['channels', 'mode'], 'open')}
                                    className={`p-3 rounded-md border text-left ${state.channels.mode === 'open' ? 'border-primary bg-primary/5' : 'border-border'}`}
                                >
                                    <div className="font-medium text-sm">Open</div>
                                    <div className="text-xs text-muted-foreground">Any channel (or none) may invoke. List below is informational.</div>
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label>
                                {state.channels.mode === 'restricted' ? 'Authorized channels' : 'Channels (informational)'}
                            </Label>
                            <ChannelMultiSelect
                                available={channels}
                                value={state.channels.authorized_channels}
                                onChange={(v) => setField(['channels', 'authorized_channels'], v)}
                            />
                            {state.channels.mode === 'restricted' && state.channels.authorized_channels.length === 0 && (
                                <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded">
                                    Warning: restricted mode with an empty list denies every request. The assistant will be unreachable.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 7: Memory */}
                {step === 7 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Memory</h3>
                        <MemoryConfigSection
                            value={state.memory}
                            onChange={(v) => setField(['memory'], v)}
                        />
                    </div>
                )}

                {/* Step 8: Governance */}
                {step === 8 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Governance</h3>
                        <GovernanceSection
                            value={state.governance}
                            onChange={(v) => setField(['governance'], v)}
                        />
                    </div>
                )}

                {/* Step 9: Review & Create */}
                {step === 9 && (
                    <div className="space-y-3">
                        <h3 className="text-base font-semibold">Review & Create</h3>
                        <Tabs defaultValue="summary">
                            <TabsList>
                                <TabsTrigger value="summary">Summary</TabsTrigger>
                                <TabsTrigger value="json">JSON preview</TabsTrigger>
                            </TabsList>
                            <TabsContent value="summary" className="space-y-2 text-sm">
                                <SummaryRow label="Assistant Name" value={state.title} />
                                <SummaryRow label="Internal id" value="(auto-generated on save)" mono />
                                <SummaryRow label="Status / Disabled" value={`${state.status} / ${state.disabled ? 'disabled' : 'enabled'}`} />
                                <SummaryRow label="OpCodes" value={`${state.opcodes.length} configured`} />
                                <SummaryRow label="Default opcode" value={state.default_opcode || '(none)'} mono />
                                <SummaryRow label="Routing mode" value={state.typed_input.intent.routing_mode} />
                                <SummaryRow label="Entity fields" value={String(Object.keys(state.typed_input.entity_schema.properties).length)} />
                                <SummaryRow label="Knowledge / Data conn." value={`${state.knowledge.length} / ${state.data_connections.length}`} />
                                <SummaryRow label="Channels" value={`${state.channels.mode} (${state.channels.authorized_channels.length} listed)`} />
                                <SummaryRow label="Memory" value={`${state.memory.strategy}, ${state.memory.max_turns} turns, ttl ${state.memory.ttl}s`} />
                                <SummaryRow label="Governance" value={`allow ${state.governance.allowed_actions.length}, deny ${state.governance.denied_actions.length}, esc ${state.governance.escalation.length}`} />
                            </TabsContent>
                            <TabsContent value="json">
                                <pre className="text-xs font-mono bg-muted p-3 rounded max-h-[300px] overflow-auto">
                                    {JSON.stringify(state, null, 2)}
                                </pre>
                            </TabsContent>
                        </Tabs>
                    </div>
                )}
            </ScrollArea>

            <div className="flex items-center justify-between mt-4">
                <Button variant="outline" disabled={step === 1} onClick={() => setStep(s => s - 1)}>Back</Button>
                <div className="flex items-center gap-2">
                    {step < 9 ? (
                        <Button
                            onClick={() => {
                                if (!canAdvance()) {
                                    addNewNotifcation('Please complete the required fields', 'warning');
                                    return;
                                }
                                setStep(s => s + 1);
                            }}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button onClick={handleCreate} disabled={creating}>
                            {creating ? 'Creating…' : 'Create'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ──────────── Small helpers / sub-components ────────────

function SummaryRow({
 label, value, mono
}: { label: string; value: string; mono?: boolean }) {
    return (
        <div className="flex gap-4 border-b pb-1">
            <span className="text-muted-foreground w-44 shrink-0">{label}</span>
            <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
        </div>
    );
}

// Knowledge + Data Connections — multi-select pickers that load from the
// Platform's existing services. Show name + description; persist the
// underlying id + description so the backend allow-list check matches what
// the RAG/API step references at runtime.

function KnowledgeEditor({
    value, onChange, options,
}: {
    value: Array<{ collection_id: string; description: string }>;
    onChange: (v: Array<{ collection_id: string; description: string }>) => void;
    options: Array<{ _id: string; name: string; description?: string }>;
}) {
    return (
        <EntityPicker
            value={value.map(v => ({
 id: v.collection_id,
description: v.description
}))}
            onChange={(next) => onChange(next.map(n => ({
 collection_id: n.id,
description: n.description
})))}
            options={options}
            placeholder="No knowledge collections selected"
        />
    );
}

function DataConnectionsEditor({
    value, onChange, options,
}: {
    value: Array<{ connection_id: string; description: string }>;
    onChange: (v: Array<{ connection_id: string; description: string }>) => void;
    options: Array<{ _id: string; name: string; description?: string }>;
}) {
    return (
        <EntityPicker
            value={value.map(v => ({
 id: v.connection_id,
description: v.description
}))}
            onChange={(next) => onChange(next.map(n => ({
 connection_id: n.id,
description: n.description
})))}
            options={options}
            placeholder="No data connections selected"
        />
    );
}

function EntityPicker({
    value, onChange, options, placeholder,
}: {
    value: Array<{ id: string; description: string }>;
    onChange: (next: Array<{ id: string; description: string }>) => void;
    options: Array<{ _id: string; name: string; description?: string }>;
    placeholder: string;
}) {
    const selectedIds = new Set(value.map(v => v.id));
    const toggle = (opt: { _id: string; name: string; description?: string }) => {
        if (selectedIds.has(opt._id)) {
            onChange(value.filter(v => v.id !== opt._id));
        } else {
            onChange([...value, {
 id: opt._id,
description: opt.description || opt.name || ''
}]);
        }
    };

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
                {value.length === 0 && <span className="text-xs text-muted-foreground italic">{placeholder}</span>}
                {value.map(v => {
                    const found = options.find(o => o._id === v.id);
                    return (
                        <Badge key={v.id} variant="secondary" className="text-xs gap-1">
                            {found?.name || v.id}
                            <button type="button" onClick={() => onChange(value.filter(x => x.id !== v.id))}>×</button>
                        </Badge>
                    );
                })}
            </div>
            <ScrollArea className="max-h-40 border rounded">
                <div className="p-2 space-y-1">
                    {options.length === 0 && (
                        <div className="text-xs text-muted-foreground italic">
                            No options found for this sub-org.
                        </div>
                    )}
                    {options.map(opt => {
                        const on = selectedIds.has(opt._id);
                        return (
                            <label key={opt._id} className="flex items-start gap-2 text-xs cursor-pointer py-1">
                                <input type="checkbox" checked={on} onChange={() => toggle(opt)} className="mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-medium">{opt.name}</div>
                                    {opt.description && (
                                        <div className="text-muted-foreground">{opt.description}</div>
                                    )}
                                    <div className="text-[10px] font-mono text-muted-foreground">{opt._id}</div>
                                </div>
                            </label>
                        );
                    })}
                </div>
            </ScrollArea>
        </div>
    );
}

// Multi-select of existing channels with free-text fallback for builders who
// know the channel_id but haven't created the channel record yet.
function ChannelMultiSelect({
    available, value, onChange,
}: {
    available: Array<{ _id: string; topic?: string; address?: string }>;
    value: string[];
    onChange: (v: string[]) => void;
}) {
    const [custom, setCustom] = useState('');

    const toggle = (id: string) => {
        if (value.includes(id)) onChange(value.filter(v => v !== id));
        else onChange([...value, id]);
    };

    const addCustom = () => {
        const trimmed = custom.trim();
        if (!trimmed) return;
        if (!value.includes(trimmed)) onChange([...value, trimmed]);
        setCustom('');
    };

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
                {value.map(id => {
                    const ch = available.find(a => a._id === id);
                    return (
                        <Badge key={id} variant="secondary" className="text-xs gap-1">
                            {ch?.topic || ch?.address || id}
                            <button type="button" onClick={() => toggle(id)}>×</button>
                        </Badge>
                    );
                })}
            </div>
            <ScrollArea className="max-h-32 border rounded">
                <div className="p-2 space-y-1">
                    {available.length === 0 && <div className="text-xs text-muted-foreground italic">No channels on this sub-org yet.</div>}
                    {available.map(ch => {
                        const checked = value.includes(ch._id);
                        return (
                            <label key={ch._id} className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={checked} onChange={() => toggle(ch._id)} />
                                <span className="font-mono">{ch._id}</span>
                                <span className="text-muted-foreground">— {ch.topic || ch.address || ''}</span>
                            </label>
                        );
                    })}
                </div>
            </ScrollArea>
            <div className="flex items-center gap-2">
                <Input
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="add channel_id manually"
                    className="text-xs"
                />
                <Button variant="outline" size="sm" onClick={addCustom}>Add</Button>
            </div>
        </div>
    );
}
